import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { DateInput } from '../components/shared/DateInput';
import { NumberInput } from '../components/shared/NumberInput';
import { useNavigate, useParams } from 'react-router';
import {
  Text, Spinner, Badge, Dropdown, Option, Button, Input, Field,
  makeStyles, tokens,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Warning24Regular, AddRegular, EditRegular, DeleteRegular, CloudAdd24Regular,
  BoxMultiple24Regular, CalendarLtr24Regular, MoneyHand24Regular,
  BoxRegular, BeakerRegular, LeafOneRegular, GasRegular,
} from '@fluentui/react-icons';
import { resizeImage } from '../utils/resize-image';

const CATEGORY_ICON: Record<string, typeof BoxRegular> = {
  AGROQUIMICO: BeakerRegular,
  FERTILIZANTE: LeafOneRegular,
  COMBUSTIBLE: GasRegular,
};
import {
  fetchWarehousesByRancho, fetchStock, fetchMovements,
  fetchProducts, createProduct, updateProduct, deleteProduct, createMovement, uploadProductImage,
  fetchNegativeStockAlerts, ignoreAlert, ignoreAllAlerts,
  type Product, type ProductInput, type ProductCategory, type ProductUnit, type MovementInput,
} from '../api/inventory.api';
import { fetchUsers } from '../api/settings.api';
import { useAuthStore } from '../store/auth.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { KpiCard } from '../components/shared/KpiCard';
import { StockDiagnosticsPanel } from '../components/inventory/StockDiagnosticsPanel';
import { ProductDetailDialog } from '../components/inventory/ProductDetailDialog';
import { TransferDialog } from '../components/inventory/TransferDialog';
import { useEntityNames } from '../hooks/useEntityNames';
import { useRanchoStore } from '../store/rancho.store';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { usePageTitle } from '../hooks/usePageTitle';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { resolveEntityName } from '../utils/entity-names';
import { usePermission } from '../hooks/usePermission';
import type { StockItem, WarehouseMovement, NegativeStockAlert, Role } from '../types/api.types';
import { WarehouseTransfersTab } from '../components/inventory/WarehouseTransfersTab';
import { PageTitle } from '../components/shared/PageTitle';
import { FilterPanel } from '../components/shared/FilterPanel';
import { SubTabs } from '../components/shared/SubTabs';

const NOTIFY_ROLES: Role[] = ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'FIELD_WORKER', 'AUDITOR', 'INVESTOR'];

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: '24px' },
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
  },
  alertBanner: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '12px 16px',
    display:         'flex',
    alignItems:      'center',
    gap:             '10px',
    color:           tokens.colorStatusWarningForeground1,
    fontSize:        '13px',
  },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px' },
  th: {
    textAlign:    'left' as const,
    padding:      '8px 12px',
    color:        tokens.colorNeutralForeground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize:     '12px',
    fontWeight:   '600',
  },
  td: {
    padding:      '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  formGrid: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '12px',
    minWidth:      '360px',
    paddingTop:    '8px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '12px',
  },
});

// ─── Constants ─────────────────────────────────────────────────────────────────

function useCategories(): { value: ProductCategory; label: string }[] {
  const t = useT();
  return [
    { value: 'AGROQUIMICO',  label: t.inventory.catAgroquimico  },
    { value: 'FERTILIZANTE', label: t.inventory.catFertilizante },
    { value: 'SEMILLA',      label: t.inventory.catSemilla      },
    { value: 'COMBUSTIBLE',  label: t.inventory.catCombustible  },
    { value: 'OTRO',         label: t.inventory.catOtro         },
  ];
}

function useUnits(): { value: ProductUnit; label: string }[] {
  const t = useT();
  return [
    { value: 'litros',    label: t.inventory.unitLitros    },
    { value: 'kg',        label: t.inventory.unitKg        },
    { value: 'piezas',    label: t.inventory.unitPiezas    },
    { value: 'toneladas', label: t.inventory.unitToneladas },
    { value: 'cajas',     label: t.inventory.unitCajas     },
  ];
}

// ─── ProductFormDialog ─────────────────────────────────────────────────────────

interface ProductFormProps {
  open:     boolean;
  onClose:  () => void;
  initial?: Product | null;
}

type PForm = {
  name:             string;
  category:         ProductCategory;
  unit:             ProductUnit;
  unitCost:         string;
  minStock:         string;
  description:      string;
  activeIngredient: string;
  registroSENASICA: string;
  notifyUserIds:    string[];
  notifyRoles:      Role[];
};

const EMPTY_FORM: PForm = {
  name: '', category: 'AGROQUIMICO', unit: 'litros',
  unitCost: '', minStock: '',
  description: '', activeIngredient: '', registroSENASICA: '',
  notifyUserIds: [], notifyRoles: [],
};

function toForm(p: Product): PForm {
  return {
    name:             p.name,
    category:         p.category,
    unit:             p.unit,
    unitCost:         p.unitCost.toString(),
    minStock:         p.minStock?.toString() ?? '',
    description:      p.description ?? '',
    activeIngredient: p.activeIngredient ?? '',
    registroSENASICA: p.registroSENASICA ?? '',
    notifyUserIds:    p.notifyUserIds ?? [],
    notifyRoles:      (p.notifyRoles as Role[]) ?? [],
  };
}

export function ProductFormDialog({ open, onClose, initial }: ProductFormProps) {
  const { activeRanchoId } = useRanchoStore();
  const s   = useStyles();
  const t   = useT();
  const CATEGORIES = useCategories();
  const UNITS = useUnits();
  const qc  = useQueryClient();
  const isEdit = !!initial;

  const [form,  setForm]  = useState<PForm>(initial ? toForm(initial) : { ...EMPTY_FORM });
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  useResetOnOpen(open, () => {
    setForm(initial ? toForm(initial) : { ...EMPTY_FORM });
    setError(''); fe.clear();
  });

  const set = (patch: Partial<PForm>) => {
    setForm(f => ({ ...f, ...patch }));
    Object.keys(patch).forEach(k => fe.clear(k));
  };

  const categoryLabel = CATEGORIES.find(c => c.value === form.category)?.label ?? form.category;
  const unitLabel     = UNITS.find(u => u.value === form.unit)?.label ?? form.unit;

  const { data: tenantUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn:  fetchUsers,
  });

  const buildInput = (): ProductInput => ({
    name:             form.name.trim(),
    category:         form.category,
    unit:             form.unit,
    unitCost:         parseFloat(form.unitCost) || 0,
    minStock:         form.minStock ? parseFloat(form.minStock) : undefined,
    description:      form.description.trim() || undefined,
    activeIngredient: form.activeIngredient.trim() || undefined,
    registroSENASICA: form.registroSENASICA.trim() || undefined,
    notifyUserIds:    form.notifyUserIds,
    notifyRoles:      form.notifyRoles,
  });

  const createMut = useMutation({
    mutationFn: () => createProduct({ ...buildInput(), ...(activeRanchoId && { ranchoId: activeRanchoId }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.inventory.createError),
  });

  const updateMut = useMutation({
    mutationFn: () => updateProduct(initial!.productId, buildInput()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.inventory.updateError),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const imageMut = useMutation({
    mutationFn: async (file: File) => uploadProductImage(initial!.productId, await resizeImage(file)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); },
    onError: () => setError(t.inventory.imageError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      name: !form.name.trim() && t.inventory.nameRequired,
      unitCost: (!form.unitCost || isNaN(parseFloat(form.unitCost))) && t.inventory.invalidCost,
    });
    if (!ok) return;
    isEdit ? updateMut.mutate() : createMut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle>{isEdit ? `${t.inventory.editProductTitlePrefix}${initial?.name}` : t.inventory.newProductDialogTitle}</DialogTitle>
          <DialogContent>
            <div className={s.formGrid}>
              <Field label={t.inventory.fieldName} {...fe.props('name')}>
                <Input
                  value={form.name}
                  onChange={(_, d) => set({ name: d.value })}
                  placeholder={t.inventory.fieldNamePlaceholder}
                  disabled={saving}
                />
              </Field>

              <div className={s.formRow}>
                <Field label={t.inventory.fieldCategory}>
                  <Dropdown
                    selectedOptions={[form.category]}
                    value={categoryLabel}
                    onOptionSelect={(_, d) => set({ category: d.optionValue as ProductCategory })}
                    disabled={saving}
                  >
                    {CATEGORIES.map(c => (
                      <Option key={c.value} value={c.value}>{c.label}</Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label={t.inventory.fieldUnit}>
                  <Dropdown
                    selectedOptions={[form.unit]}
                    value={unitLabel}
                    onOptionSelect={(_, d) => set({ unit: d.optionValue as ProductUnit })}
                    disabled={saving}
                  >
                    {UNITS.map(u => (
                      <Option key={u.value} value={u.value}>{u.label}</Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>

              <div className={s.formRow}>
                <Field label={t.inventory.fieldUnitCost} {...fe.props('unitCost')}>
                  <NumberInput
                    value={form.unitCost}
                    onValueChange={(v) => set({ unitCost: v })}
                    placeholder={t.inventory.fieldUnitCostPlaceholder}
                    disabled={saving}
                  />
                </Field>
                <Field label={t.inventory.fieldMinStock}>
                  <NumberInput
                    value={form.minStock}
                    onValueChange={(v) => set({ minStock: v })}
                    placeholder={t.inventory.fieldMinStockPlaceholder}
                    disabled={saving}
                  />
                </Field>
              </div>

              <Field label={t.inventory.fieldActiveIngredient}>
                <Input
                  value={form.activeIngredient}
                  onChange={(_, d) => set({ activeIngredient: d.value })}
                  placeholder={t.inventory.fieldActiveIngredientPlaceholder}
                  disabled={saving}
                />
              </Field>

              <Field label={t.inventory.fieldSenasica}>
                <Input
                  value={form.registroSENASICA}
                  onChange={(_, d) => set({ registroSENASICA: d.value })}
                  placeholder={t.inventory.fieldSenasicaPlaceholder}
                  disabled={saving}
                />
              </Field>

              <Field label={t.inventory.fieldDescription}>
                <Input
                  value={form.description}
                  onChange={(_, d) => set({ description: d.value })}
                  placeholder={t.inventory.fieldDescriptionPlaceholder}
                  disabled={saving}
                />
              </Field>

              <Field label={t.inventory.fieldNotifyUsers} hint={t.inventory.fieldNotifyUsersHint}>
                <Dropdown
                  multiselect
                  selectedOptions={form.notifyUserIds}
                  value={form.notifyUserIds
                    .map(pn => tenantUsers.find(u => u.phoneNumber === pn)?.name ?? pn)
                    .join(', ')}
                  onOptionSelect={(_, d) => set({ notifyUserIds: d.selectedOptions })}
                  placeholder={t.inventory.fieldNotifyUsersPlaceholder}
                  disabled={saving}
                  style={{ width: '100%' }}
                >
                  {tenantUsers.map(u => (
                    <Option key={u.phoneNumber} value={u.phoneNumber}>{u.name ?? u.phoneNumber}</Option>
                  ))}
                </Dropdown>
              </Field>

              <Field label={t.inventory.fieldNotifyRoles}>
                <Dropdown
                  multiselect
                  selectedOptions={form.notifyRoles}
                  value={form.notifyRoles.map(r => t.settings.roles[r] ?? r).join(', ')}
                  onOptionSelect={(_, d) => set({ notifyRoles: d.selectedOptions as Role[] })}
                  placeholder={t.inventory.fieldNotifyRolesPlaceholder}
                  disabled={saving}
                  style={{ width: '100%' }}
                >
                  {NOTIFY_ROLES.map(r => (
                    <Option key={r} value={r}>{t.settings.roles[r]}</Option>
                  ))}
                </Dropdown>
              </Field>

              {isEdit && (
                <Field label={t.inventory.productImage}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    aria-label={t.inventory.uploadImage}
                    disabled={saving || imageMut.isPending}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) imageMut.mutate(file);
                    }}
                  />
                </Field>
              )}

              {error && (
                <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>{t.inventory.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={saving}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {isEdit ? t.inventory.saveChanges : t.inventory.createProduct}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── DeleteProductDialog ───────────────────────────────────────────────────────

interface DeleteProductProps {
  product: Product | null;
  onClose: () => void;
}

function DeleteProductDialog({ product, onClose }: DeleteProductProps) {
  const t   = useT();
  const qc  = useQueryClient();
  const mut = useMutation({
    mutationFn: () => deleteProduct(product!.productId),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['products'] }); onClose(); },
  });

  return (
    <Dialog open={!!product} onOpenChange={(_, d) => !d.open && onClose()} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t.inventory.deleteProductTitle}</DialogTitle>
          <DialogContent>
            <Text>
              {t.inventory.deleteProductConfirmPrefix}<strong>{product?.name}</strong>{t.inventory.deleteProductConfirmSuffix}
            </Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.inventory.cancel}</Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.inventory.delete}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── MovementFormDialog ────────────────────────────────────────────────────────

interface MovementFormProps {
  open:        boolean;
  onClose:     () => void;
  warehouseId: string;
  products:    Product[];
}

type MForm = {
  type:        'IN' | 'OUT';
  productId:   string;
  qty:         string;
  reason:      string;
  expiryDate:  string;
  loteNumber:  string;
};

/** Ventana (días) para marcar un producto como "por vencer". */
export const EXPIRING_SOON_DAYS = 30;
const isExpiringSoonDate = (expiryDate?: string | null): boolean =>
  !!expiryDate && new Date(expiryDate) <= new Date(Date.now() + EXPIRING_SOON_DAYS * 86_400_000);

const EMPTY_MOVEMENT: MForm = { type: 'IN', productId: '', qty: '', reason: '', expiryDate: '', loteNumber: '' };

function MovementFormDialog({ open, onClose, warehouseId, products }: MovementFormProps) {
  const s  = useStyles();
  const t  = useT();
  const qc = useQueryClient();

  const [form,  setForm]  = useState<MForm>({ ...EMPTY_MOVEMENT });
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const set = (patch: Partial<MForm>) => {
    setForm(f => ({ ...f, ...patch }));
    Object.keys(patch).forEach(k => fe.clear(k));
  };

  const selectedProduct = products.find(p => p.productId === form.productId);

  const mut = useMutation({
    mutationFn: () => {
      const input: MovementInput = {
        productId: form.productId,
        type:      form.type,
        qty:       parseFloat(form.qty) || 0,
        reason:    form.reason.trim(),
        expiryDate: form.expiryDate || undefined,
        loteNumber: form.loteNumber.trim() || undefined,
      };
      return createMovement(warehouseId, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock', warehouseId] });
      qc.invalidateQueries({ queryKey: ['movements', warehouseId] });
      setForm({ ...EMPTY_MOVEMENT }); fe.clear();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.inventory.createMovementError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      productId: !form.productId && t.inventory.productRequired,
      qty: (!form.qty || parseFloat(form.qty) <= 0) && t.inventory.qtyRequired,
      reason: !form.reason.trim() && t.inventory.reasonRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { setForm({ ...EMPTY_MOVEMENT }); fe.clear(); setError(''); onClose(); } }} modalType="modal">
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="inventory"
              voiceExample={t.telegramHelp.inventoryVoice}
              voiceDesc={t.telegramHelp.inventoryDesc}
            />
          }>{t.inventory.newMovementDialogTitle}</DialogTitle>
          <DialogContent>
            <div className={s.formGrid}>
              <div className={s.formRow}>
                <Field label={t.inventory.fieldMovementType}>
                  <Dropdown
                    selectedOptions={[form.type]}
                    value={form.type === 'IN' ? t.inventory.movementTypeIn : t.inventory.movementTypeOut}
                    onOptionSelect={(_, d) => set({ type: d.optionValue as 'IN' | 'OUT' })}
                    disabled={mut.isPending}
                  >
                    <Option value="IN">{t.inventory.movementTypeIn}</Option>
                    <Option value="OUT">{t.inventory.movementTypeOut}</Option>
                  </Dropdown>
                </Field>
                <Field label={t.inventory.fieldProduct} {...fe.props('productId')}>
                  <Dropdown
                    selectedOptions={form.productId ? [form.productId] : []}
                    value={selectedProduct?.name ?? t.inventory.selectProduct}
                    onOptionSelect={(_, d) => set({ productId: (d.optionValue as string) ?? '' })}
                    disabled={mut.isPending}
                  >
                    {products.map(p => (
                      <Option key={p.productId} value={p.productId}>{p.name}</Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>

              <Field label={`${t.inventory.fieldQty}${selectedProduct ? ` (${selectedProduct.unit})` : ''}`} {...fe.props('qty')}>
                <NumberInput
                  value={form.qty}
                  onValueChange={(v) => set({ qty: v })}
                  placeholder={t.inventory.fieldQtyPlaceholder}
                  disabled={mut.isPending}
                />
              </Field>

              <Field label={t.inventory.fieldReason} {...fe.props('reason')}>
                <Input
                  value={form.reason}
                  onChange={(_, d) => set({ reason: d.value })}
                  placeholder={t.inventory.fieldReasonPlaceholder}
                  disabled={mut.isPending}
                />
              </Field>

              {form.type === 'IN' && (
                <div className={s.formRow}>
                  <Field label={t.inventory.fieldExpiryDate}>
                    <DateInput
                      value={form.expiryDate}
                      onValueChange={(v) => set({ expiryDate: v })}
                      disabled={mut.isPending}
                    />
                  </Field>
                  <Field label={t.inventory.fieldLoteNumber}>
                    <Input
                      value={form.loteNumber}
                      onChange={(_, d) => set({ loteNumber: d.value })}
                      placeholder={t.inventory.fieldLoteNumberPlaceholder}
                      disabled={mut.isPending}
                    />
                  </Field>
                </div>
              )}

              {error && (
                <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.inventory.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {t.inventory.registerMovement}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── InventoryPage ────────────────────────────────────────────────────────────

type Tab = 'stock' | 'movements' | 'transfers' | 'catalog' | 'alerts';

function useTabLabels(): Record<Tab, string> {
  const t = useT();
  return {
    stock:     t.inventory.tabStock,
    movements: t.inventory.tabMovements,
    transfers: t.inventory.tabTransfers,
    catalog:   t.inventory.tabCatalog,
    alerts:    t.inventory.tabAlerts,
  };
}

// ─── AlertsTab ─────────────────────────────────────────────────────────────────

interface AlertsTabProps {
  alerts:            NegativeStockAlert[];
  warehouseId:       string;
  canWrite:          boolean;
  onOpenDiagnostics: (alert: NegativeStockAlert) => void;
  productName:       (productId: string) => string;
}

function AlertsTab({ alerts, warehouseId, canWrite, onOpenDiagnostics, productName }: AlertsTabProps) {
  const s  = useStyles();
  const { formatDate } = useLocaleSettings();
  const t  = useT();
  const { fmtNum } = useIntlFormat();
  const qc = useQueryClient();

  const [ignoreTarget, setIgnoreTarget] = useState<NegativeStockAlert | null>(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const feIgnore = useFieldErrors();
  const feIgnoreAll = useFieldErrors();
  const [ignoreAllOpen, setIgnoreAllOpen] = useState(false);
  const [ignoreAllReason, setIgnoreAllReason] = useState('');

  const ignoreMut = useMutation({
    mutationFn: (reason: string) => ignoreAlert(ignoreTarget!.warehouseId, ignoreTarget!.productId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['negative-stock-alerts'] });
      setIgnoreTarget(null);
      setIgnoreReason('');
    },
  });

  const ignoreAllMut = useMutation({
    mutationFn: (reason: string) => ignoreAllAlerts(warehouseId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['negative-stock-alerts'] });
      setIgnoreAllOpen(false);
      setIgnoreAllReason('');
    },
  });

  return (
    <div className={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text weight="semibold">{t.inventory.tabAlerts}</Text>
        {canWrite && alerts.length > 0 && (
          <Button appearance="outline" onClick={() => setIgnoreAllOpen(true)}>
            {t.inventory.ignoreAllAlerts}
          </Button>
        )}
      </div>

      {alerts.length === 0 ? (
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.noAlerts}</Text>
      ) : (
        <table className={s.table}>
          <thead>
            <tr>
              <th className={s.th}>{t.inventory.tableProduct}</th>
              <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.tableQuantity}</th>
              <th className={s.th}>{t.inventory.alertSince}</th>
              <th className={s.th} style={{ textAlign: 'center' }}>{t.inventory.colActions}</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr key={`${a.warehouseId}-${a.productId}`}>
                <td className={s.td} style={{ fontWeight: 500 }}>{productName(a.productId)}</td>
                <td className={s.td} style={{ textAlign: 'right', color: tokens.colorStatusDangerForeground1, fontWeight: 600 }}>
                  {fmtNum(a.currentQtyAtDetection)}
                </td>
                <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{formatDate(a.firstDetectedAt.slice(0, 10))}</td>
                <td className={s.td} style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    <Button size="small" appearance="subtle" onClick={() => onOpenDiagnostics(a)}>
                      {t.inventory.resolveAlert}
                    </Button>
                    {canWrite && (
                      <Button size="small" appearance="subtle" onClick={() => setIgnoreTarget(a)}>
                        {t.inventory.ignoreAlert}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Ignorar una alerta */}
      <Dialog open={!!ignoreTarget} onOpenChange={(_, d) => { if (!d.open) { setIgnoreTarget(null); setIgnoreReason(''); feIgnore.clear(); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.inventory.ignoreDialogTitle}</DialogTitle>
            <DialogContent>
              <Field label={t.inventory.ignoreReasonLabel} required {...feIgnore.props('reason')}>
                <Input
                  value={ignoreReason}
                  onChange={(_, d) => { setIgnoreReason(d.value); feIgnore.clear('reason'); }}
                  placeholder={t.inventory.ignoreReasonPlaceholder}
                  disabled={ignoreMut.isPending}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setIgnoreTarget(null); setIgnoreReason(''); feIgnore.clear(); }} disabled={ignoreMut.isPending}>
                {t.inventory.cancel}
              </Button>
              <Button
                appearance="primary"
                onClick={() => { if (feIgnore.validate({ reason: !ignoreReason.trim() && t.common.validation.fieldRequired })) ignoreMut.mutate(ignoreReason); }}
                disabled={ignoreMut.isPending}
                icon={ignoreMut.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {t.inventory.confirmIgnore}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Ignorar todas */}
      <Dialog open={ignoreAllOpen} onOpenChange={(_, d) => { if (!d.open) { setIgnoreAllOpen(false); setIgnoreAllReason(''); feIgnoreAll.clear(); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t.inventory.ignoreAllDialogTitle}</DialogTitle>
            <DialogContent>
              <Text size={200}>{t.inventory.ignoreAllConfirmWarehouse}</Text>
              <Field label={t.inventory.ignoreReasonLabel} required style={{ marginTop: '12px' }} {...feIgnoreAll.props('reason')}>
                <Input
                  value={ignoreAllReason}
                  onChange={(_, d) => { setIgnoreAllReason(d.value); feIgnoreAll.clear('reason'); }}
                  placeholder={t.inventory.ignoreReasonPlaceholder}
                  disabled={ignoreAllMut.isPending}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setIgnoreAllOpen(false); setIgnoreAllReason(''); feIgnoreAll.clear(); }} disabled={ignoreAllMut.isPending}>
                {t.inventory.cancel}
              </Button>
              <Button
                appearance="primary"
                onClick={() => { if (feIgnoreAll.validate({ reason: !ignoreAllReason.trim() && t.common.validation.fieldRequired })) ignoreAllMut.mutate(ignoreAllReason); }}
                disabled={ignoreAllMut.isPending}
                icon={ignoreAllMut.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {t.inventory.confirmIgnore}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

export function InventoryPage() {
  const { activeRanchoId } = useRanchoStore();
  const s = useStyles();
  const t = useT();
  const { fmtNum } = useIntlFormat();
  usePageTitle(t.pageTitle.inventory);
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const CATEGORIES = useCategories();
  const TAB_LABELS = useTabLabels();
  const { user } = useAuthStore();
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { loteName } = useEntityNames();
  const [detailItem, setDetailItem] = useState<StockItem | null>(null);
  const isAdmin   = user?.roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN') ?? false;
  // Roles con permiso inventory:write (ver DEFAULT_ROLE_PERMISSIONS en rbac.model.ts):
  // además de ADMIN/SUPER_ADMIN, SUPERVISOR y WAREHOUSE_MANAGER pueden registrar movimientos.
  const canWriteMovements = user?.roles.some(r => ['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR', 'WAREHOUSE_MANAGER'].includes(r)) ?? false;
  const canBulkImport = usePermission('admin:bulk-import');
  const canBulkUploadStock = usePermission('inventory:write');
  // Traspasos entre bodegas: permiso propio `warehouse:transfer` (ADMIN, WAREHOUSE_MANAGER, SUPER_ADMIN).
  const canTransfer = usePermission('warehouse:transfer');
  // Pestaña Traspasos (historial): visible con inventory:read, el mismo permiso que ver inventario.
  const canReadInventory = usePermission('inventory:read');
  const [transferOpen, setTransferOpen] = useState(false);

  const [tab, setTab]                       = useState<Tab>('stock');
  const [warehouseId, setWarehouseId]       = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<ProductCategory[]>([]);

  // Catalog CRUD state
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editProduct,     setEditProduct]     = useState<Product | null>(null);
  const [deleteTarget,    setDeleteTarget]    = useState<Product | null>(null);

  // Movement (stock entry/exit) state
  const [movementFormOpen, setMovementFormOpen] = useState(false);

  // Negative stock alert queue state
  const [diagnosticsTarget, setDiagnosticsTarget] = useState<NegativeStockAlert | null>(null);

  const { data: warehouses = [] } = useQuery({ queryKey: ['warehouses', activeRanchoId], queryFn: () => fetchWarehousesByRancho(activeRanchoId) });
  // Opción A: una bodega sin rancho es de empresa (aparece en todos los ranchos).
  const warehouseLabel = (w: { name: string; ranchoId?: string | null }) =>
    w.ranchoId ? w.name : `${w.name} · ${t.inventory.warehouseShared}`;
  // A warehouse picked under another rancho is ignored once the active rancho changes.
  // Por defecto: la bodega propia del rancho activo; si no hay, la primera disponible
  // (antes era la primera de la lista, que suele ser una bodega compartida vacía).
  const defaultWarehouse = warehouses.find((w) => w.ranchoId) ?? warehouses[0];
  const activeWarehouseId = warehouses.some((w) => w.warehouseId === warehouseId)
    ? warehouseId
    : (defaultWarehouse?.warehouseId ?? '');

  const { data: stock = [], isLoading: stockLoading } = useQuery<StockItem[]>({
    queryKey: ['stock', activeWarehouseId],
    queryFn:  () => fetchStock(activeWarehouseId),
    enabled:  !!activeWarehouseId && tab === 'stock',
  });

  const { data: movements = [], isLoading: movLoading } = useQuery<WarehouseMovement[]>({
    queryKey: ['movements', activeWarehouseId],
    queryFn:  () => fetchMovements(activeWarehouseId),
    enabled:  !!activeWarehouseId && tab === 'movements',
  });

  const { data: catalogProducts = [], isLoading: prodLoading } = useQuery<Product[]>({
    queryKey: ['products', activeRanchoId],
    queryFn:  () => fetchProducts(undefined, activeRanchoId),
    enabled:  tab === 'catalog',
  });
  const products = categoryFilter.length ? catalogProducts.filter(p => categoryFilter.includes(p.category)) : catalogProducts;

  const { data: allProducts = [] } = useQuery<Product[]>({
    queryKey: ['products', '', activeRanchoId],
    queryFn:  () => fetchProducts(undefined, activeRanchoId),
    enabled:  tab !== 'catalog',
  });

  const { data: alerts = [] } = useQuery<NegativeStockAlert[]>({
    queryKey: ['negative-stock-alerts'],
    queryFn:  fetchNegativeStockAlerts,
  });

  const lowStock = stock.filter(
    (i) => i.lowStockThreshold != null && i.quantity <= i.lowStockThreshold,
  );
  const expiringSoon = stock.filter(
    (i) => isExpiringSoonDate(i.expiryDate),
  );
  const totalStockValue = stock.reduce((sum, i) => sum + (i.totalCost ?? 0), 0);

  const openCreate = () => { setEditProduct(null); setProductFormOpen(true); };
  const openEdit   = (p: Product) => { setEditProduct(p); setProductFormOpen(true); };

  return (
    <div className={s.root}>
      <PageTitle>{t.inventory.title}</PageTitle>

      <SubTabs
        selected={tab}
        onSelect={(v) => setTab(v as Tab)}
        tabs={(['stock', 'movements', 'transfers', 'catalog', 'alerts'] as Tab[])
          .filter((tabKey) => tabKey !== 'transfers' || canReadInventory)
          .map((tabKey) => ({
            value: tabKey,
            label: tabKey === 'alerts' && alerts.length > 0 ? `${TAB_LABELS[tabKey]} (${alerts.length})` : TAB_LABELS[tabKey],
          }))}
      />

      {/* Toolbar */}
      <div className={s.toolbar}>
        {/* Warehouse selector — only for stock/movements tabs */}
        {tab !== 'catalog' && tab !== 'transfers' && (
          <Dropdown
            value={(() => { const w = warehouses.find((x) => x.warehouseId === activeWarehouseId); return w ? warehouseLabel(w) : t.inventory.selectWarehouse; })()}
            onOptionSelect={(_, d) => setWarehouseId(d.optionValue as string)}
            style={{ minWidth: '200px' }}
          >
            {warehouses.map((w) => (
              <Option key={w.warehouseId} value={w.warehouseId} text={warehouseLabel(w)}>{warehouseLabel(w)}</Option>
            ))}
          </Dropdown>
        )}

        {/* Category filter — only for catalog tab */}
        {tab === 'catalog' && (
          <FilterPanel
            onClearAll={() => setCategoryFilter([])}
            dimensions={[{
              key: 'category',
              label: t.inventory.colCategory,
              options: CATEGORIES.map(c => ({ value: c.value, label: c.label })),
              selected: categoryFilter,
              onApply: (v) => setCategoryFilter(v as ProductCategory[]),
              onClear: () => setCategoryFilter([]),
            }]}
          />
        )}

        {tab === 'catalog' && (canBulkImport || isAdmin) && (
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {canBulkImport && (
              <Button appearance="outline" icon={<CloudAdd24Regular />} onClick={() => navigate(`/${tenantId}/bulk-import`)}>
                {t.nav.bulkImport}
              </Button>
            )}
            {isAdmin && (
              <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                {t.inventory.newProduct}
              </Button>
            )}
          </div>
        )}

        {tab !== 'catalog' && tab !== 'alerts' && tab !== 'transfers' && (canBulkUploadStock || canWriteMovements || canTransfer) && (
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {canBulkUploadStock && (
              <Button appearance="outline" icon={<CloudAdd24Regular />} onClick={() => navigate(`/${tenantId}/inventarios`)}>
                {t.nav.inventories}
              </Button>
            )}
            {canTransfer && warehouses.length > 1 && (
              <Button appearance="outline" onClick={() => setTransferOpen(true)}>
                {t.inventory.transferButton}
              </Button>
            )}
            {canWriteMovements && (
              <Button appearance="primary" icon={<AddRegular />} onClick={() => setMovementFormOpen(true)}>
                {t.inventory.newMovement}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* KPI widgets (stock tab only) */}
      {tab === 'stock' && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <KpiCard
            label={t.inventory.kpiTotalValue}
            value={formatCurrency(totalStockValue)}
            icon={<MoneyHand24Regular />}
            iconBg={tokens.colorBrandBackground2}
            iconColor={tokens.colorBrandForeground1}
          />
          <KpiCard
            label={t.inventory.kpiLowStock}
            value={lowStock.length}
            tone={lowStock.length > 0 ? 'danger' : 'default'}
            icon={<BoxMultiple24Regular />}
            iconBg={tokens.colorStatusDangerBackground1}
            iconColor={tokens.colorStatusDangerForeground1}
          />
          <KpiCard
            label={t.inventory.kpiExpiringSoon}
            value={expiringSoon.length}
            tone={expiringSoon.length > 0 ? 'danger' : 'default'}
            icon={<CalendarLtr24Regular />}
            iconBg={tokens.colorStatusWarningBackground1}
            iconColor={tokens.colorStatusWarningForeground1}
          />
        </div>
      )}

      {/* Low-stock alert (stock tab only) */}
      {tab === 'stock' && lowStock.length > 0 && (
        <div className={s.alertBanner}>
          <Warning24Regular />
          <span>
            <strong>{lowStock.length}</strong> {t.inventory.lowStockAlert}:{' '}
            {lowStock.map((i) => i.productName).join(', ')}
          </span>
        </div>
      )}

      {/* ── Stock tab ─────────────────────────────────────────────────────────── */}
      {tab === 'stock' && (
        <div className={s.card}>
          <Text weight="semibold">{t.inventory.tabStock} (FEFO)</Text>
          {stockLoading ? (
            <Spinner size="small" label={t.inventory.loadingStock} />
          ) : stock.length === 0 ? (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.noStock}</Text>
          ) : (
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{t.inventory.tableProduct}</th>
                  <th className={s.th}>{t.inventory.tableCategory}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.tableQuantity}</th>
                  <th className={s.th}>{t.inventory.tableUnit}</th>
                  <th className={s.th}>{t.inventory.tableExpiry}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.tableCost}</th>
                  <th className={s.th}>{t.common.status}</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((item) => {
                  const isLow = item.lowStockThreshold != null && item.quantity <= item.lowStockThreshold;
                  const isExpiringSoon = isExpiringSoonDate(item.expiryDate);
                  return (
                    <tr key={`${item.warehouseId}-${item.productId}`}>
                      <td className={s.td} style={{ fontWeight: '500' }}>
                        <Button appearance="transparent" size="small" aria-label={`${t.inventory.productDetailOpen}: ${item.productName}`} onClick={() => setDetailItem(item)}>
                          {item.productName}
                        </Button>
                      </td>
                      <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{CATEGORIES.find(c => c.value === item.category)?.label ?? item.category ?? '—'}</td>
                      <td className={s.td} style={{ textAlign: 'right', fontWeight: '600', color: isLow ? tokens.colorStatusDangerForeground1 : undefined }}>
                        {fmtNum(item.quantity)}
                      </td>
                      <td className={s.td}>{item.unit}</td>
                      <td className={s.td} style={{ color: isExpiringSoon ? tokens.colorStatusWarningForeground1 : undefined }}>
                        {item.expiryDate ?? '—'}
                      </td>
                      <td className={s.td} style={{ textAlign: 'right' }}>
                        {item.totalCost != null ? formatCurrency(item.totalCost) : '—'}
                      </td>
                      <td className={s.td}>
                        {isLow ? (
                          <Badge appearance="tint" color="danger"   size="small">{t.inventory.stockLow}</Badge>
                        ) : isExpiringSoon ? (
                          <Badge appearance="tint" color="warning"  size="small">{t.inventory.stockExpiring}</Badge>
                        ) : (
                          <Badge appearance="tint" color="success"  size="small">{t.inventory.stockOk}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ProductDetailDialog item={detailItem} warehouseId={activeWarehouseId} onClose={() => setDetailItem(null)} />

      {/* ── Movements tab ────────────────────────────────────────────────────── */}
      {tab === 'movements' && (
        <div className={s.card}>
          <Text weight="semibold">{t.inventory.tabMovements}</Text>
          {movLoading ? (
            <Spinner size="small" label={t.inventory.loadingMovements} />
          ) : movements.length === 0 ? (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.noMovements}</Text>
          ) : (
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{t.inventory.tableDate}</th>
                  <th className={s.th}>{t.inventory.tableType}</th>
                  <th className={s.th}>{t.inventory.tableReason}</th>
                  <th className={s.th}>{t.inventory.tableProduct}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.tableQuantity}</th>
                  <th className={s.th}>{t.inventory.tableUnit}</th>
                  <th className={s.th}>{t.inventory.tableRef}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.tableCost}</th>
                </tr>
              </thead>
              <tbody>
                {(movements as WarehouseMovement[]).map((m) => (
                  <tr key={m.movementId}>
                    <td className={s.td}>{formatDate((m.occurredAt ?? '').slice(0, 10))}</td>
                    <td className={s.td}>
                      <Badge appearance="tint" color={m.type === 'ENTRY' ? 'success' : 'warning'} size="small">
                        {t.inventory.movementTypes[m.type as keyof typeof t.inventory.movementTypes] ?? m.type}
                      </Badge>
                    </td>
                    <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>
                      {t.inventory.reasonLabels[m.reason as keyof typeof t.inventory.reasonLabels] ?? m.reason}
                    </td>
                    <td className={s.td}>{m.productName}</td>
                    <td className={s.td} style={{ textAlign: 'right', fontWeight: '600' }}>
                      {fmtNum(m.quantity)}
                    </td>
                    <td className={s.td}>{m.unit}</td>
                    <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{m.loteId ? (loteName(m.loteId) || '—') : (m.equipmentName ?? '—')}</td>
                    <td className={s.td} style={{ textAlign: 'right' }}>
                      {m.cost != null ? formatCurrency(m.cost) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Transfers tab ─────────────────────────────────────────────────────── */}
      {tab === 'transfers' && canReadInventory && (
        <div className={s.card}>
          <WarehouseTransfersTab />
        </div>
      )}

      {/* ── Catalog tab ───────────────────────────────────────────────────────── */}
      {tab === 'catalog' && (
        <div className={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text weight="semibold">{t.inventory.catalogTitle}</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {products.length} {products.length !== 1 ? t.inventory.productPlural : t.inventory.productSingular}
            </Text>
          </div>

          {prodLoading ? (
            <Spinner size="small" label={t.inventory.loadingCatalog} />
          ) : products.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
              <p>{t.inventory.noProductsPrefix}{categoryFilter.length ? t.inventory.noProductsFilterSuffix : ''}{t.inventory.noProductsSuffix}</p>
              {isAdmin && (
                <Button appearance="primary" icon={<AddRegular />} onClick={openCreate} style={{ marginTop: '8px' }}>
                  {t.inventory.addFirstProduct}
                </Button>
              )}
            </div>
          ) : (
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{t.inventory.colImage}</th>
                  <th className={s.th}>{t.inventory.colName}</th>
                  <th className={s.th}>{t.inventory.colCategory}</th>
                  <th className={s.th}>{t.inventory.colUnit}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.colUnitCost}</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>{t.inventory.colMinStock}</th>
                  <th className={s.th}>{t.inventory.colActiveIngredient}</th>
                  <th className={s.th}>{t.inventory.colSenasica}</th>
                  {isAdmin && <th className={s.th} style={{ textAlign: 'center' }}>{t.inventory.colActions}</th>}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const catLabel = CATEGORIES.find(c => c.value === p.category)?.label ?? p.category;
                  const CatIcon = CATEGORY_ICON[p.category] ?? BoxRegular;
                  return (
                    <tr key={p.productId}>
                      <td className={s.td}>
                        {p.thumbnailUrl ? (
                          <img
                            src={p.thumbnailUrl}
                            alt={p.name}
                            width={40}
                            height={40}
                            style={{ objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                          />
                        ) : (
                          <CatIcon fontSize={24} aria-hidden="true" style={{ color: tokens.colorNeutralForeground3 }} />
                        )}
                      </td>
                      <td className={s.td} style={{ fontWeight: '500' }}>
                        {p.name}
                        {p.description && (
                          <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{p.description}</div>
                        )}
                      </td>
                      <td className={s.td}>
                        <Badge appearance="tint" color="informative" size="small">{catLabel}</Badge>
                      </td>
                      <td className={s.td} style={{ color: tokens.colorNeutralForeground3 }}>{p.unit}</td>
                      <td className={s.td} style={{ textAlign: 'right', fontWeight: '600' }}>
                        {formatCurrency(p.unitCost)}
                      </td>
                      <td className={s.td} style={{ textAlign: 'right', color: tokens.colorNeutralForeground3 }}>
                        {p.minStock ?? '—'}
                      </td>
                      <td className={s.td} style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>
                        {p.activeIngredient ?? '—'}
                      </td>
                      <td className={s.td} style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>
                        {p.registroSENASICA ?? '—'}
                      </td>
                      {isAdmin && (
                        <td className={s.td} style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<EditRegular />}
                              onClick={() => openEdit(p)}
                            />
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<DeleteRegular />}
                              style={{ color: tokens.colorStatusDangerForeground1 }}
                              onClick={() => setDeleteTarget(p)}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Alerts tab ────────────────────────────────────────────────────────── */}
      {tab === 'alerts' && (
        <AlertsTab
          alerts={alerts}
          warehouseId={activeWarehouseId}
          canWrite={canWriteMovements}
          onOpenDiagnostics={(a) => setDiagnosticsTarget(a)}
          productName={(id) => resolveEntityName(id, new Map(products.map(p => [p.productId, p.name])))}
        />
      )}

      {/* ── Dialogs ───────────────────────────────────────────────────────────── */}
      <ProductFormDialog
        key={editProduct?.productId ?? 'new-product'}
        open={productFormOpen}
        onClose={() => setProductFormOpen(false)}
        initial={editProduct}
      />
      <DeleteProductDialog
        product={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
      <MovementFormDialog
        open={movementFormOpen}
        onClose={() => setMovementFormOpen(false)}
        warehouseId={activeWarehouseId}
        products={allProducts}
      />
      {canTransfer && (
        <TransferDialog
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          warehouses={warehouses.map(w => ({ warehouseId: w.warehouseId, name: warehouseLabel(w) }))}
          products={allProducts}
          defaultFromWarehouseId={activeWarehouseId}
        />
      )}
      {diagnosticsTarget && (
        <StockDiagnosticsPanel
          warehouseId={diagnosticsTarget.warehouseId}
          productId={diagnosticsTarget.productId}
          open
          onClose={() => setDiagnosticsTarget(null)}
        />
      )}
    </div>
  );
}
